import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from '@emotion/styled';
import Button from '@atlaskit/button';
import Page from '@atlaskit/page';
import copy from 'copy-to-clipboard';
import { colors, gridSize } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import SelectOnClickText from './SelectOnClickText';
import TallButton from './TallButton';
import RecoveryIcon from '../images/save-recovery-key.svg';
import { addFlag } from '../actions/flag-actions';
import messages from './RecoveryPage.messages';
import { Flag } from '../types';
import DropdownMenu, { DropdownItemGroup, DropdownItem } from '@atlaskit/dropdown-menu';
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import { useAppDispatch } from '../stores/redux.store';
import Heading from '@atlaskit/heading';
import { Box, Inline, Stack } from '@atlaskit/primitives';
import { cssMap } from '@atlaskit/css';
import Image from '@atlaskit/image';
import { Hide, Show } from '@atlaskit/primitives/responsive';

const imageWidth = 359;

const RecoveryCode = styled.div`
  justify-content: space-between;
  font: ${token('font.code')};
  font-size: 24px;
  font-weight: bold;
  color: ${colors.N300};
  margin-top: ${gridSize() * 2}px;
`;

const MobileRecoveryCode = styled(SelectOnClickText)`
  text-align: center;
  font: ${token('font.code')};
  font-size: 20px;
  color: ${colors.N300};
  margin-top: ${gridSize() * 2}px;
`;

const DropdownMenuContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const StyledButton = styled(Button)`
  font-size: 14px !important;
  margin-left: auto !important;
  padding: 0 16px !important;
`;

const styles = cssMap({
  container: {
    alignItems: 'center',
    width: '100%',
    maxWidth: '656px',
    marginInline: 'auto',
  },
  heading: {
    marginTop: token('space.500'),
  },
  orderedList: {
    listStyle: 'none',
    paddingLeft: 0,
    width: '65%',
  },
  listItem: {
    marginTop: token('space.200'),
    width: '100%',
  },
  paragraph: {
    paddingBlockStart: token('space.100'),
  },
});

type RecoveryPageProps = {
  onComplete: () => void;
  recoveryCode: string;
  isEmbedded: boolean;
};

const RecoveryPage = ({ isEmbedded, recoveryCode, onComplete }: RecoveryPageProps) => {
  const [newCodeSaved, setNewCodeSaved] = useState(false);
  const dispatch = useAppDispatch();

  useEffect(() => {
    window.onbeforeunload = null;
  }, []);

  const onClickOnRecovery = useCallback(() => {
    const success = copy(recoveryCode);

    if (success) {
      const flag: Flag = {
        type: 'success',
        title: messages.mfRecoveryCopiedTitle,
        description: messages.mfRecoveryCopiedDesc,
      };

      dispatch(addFlag(flag.type, flag.title, flag.description));

      setNewCodeSaved(true);
    }
  }, [dispatch, recoveryCode]);

  const onRecoveryPrint = useCallback(() => {
    // Invocation of this method will freeze the page in React 16 in development mode.
    // It does not affect production mode. The development mode bug is fixed in React 17:
    // https://github.com/facebook/react/issues/16734
    setTimeout(window.print);
    setNewCodeSaved(true);
  }, []);

  const onRecoverySkip = useCallback(() => {
    setNewCodeSaved(true);
  }, []);

  const renderDesktop = () => {
    return (
      <Stack alignInline="center" alignBlock="center">
        <Box xcss={styles.heading}>
          <Heading size="large" as="h1">
            <FormattedMessage
              id="mf.recovery.heading"
              defaultMessage="We created a new emergency recovery key for you"
              description="Heading for the recovery page on desktop"
            />
          </Heading>
        </Box>
        <Stack as="ol" alignInline="center" xcss={styles.orderedList}>
          <Box as="li" xcss={styles.listItem}>
            <Heading size="medium" as="h2">
              <FormattedMessage
                id="mf.recovery.savekey"
                defaultMessage="1. Save this emergency recovery key"
                description="The first step on the recovery page, asking to save the emergency recovery key"
              />
            </Heading>
            <Box xcss={styles.paragraph}>
              <FormattedMessage
                id="mf.recovery.savekey.desc"
                defaultMessage="If you lose access to your phone, you won't be able to log in to your account without this key. Print, copy or write down this key without letting anyone see it."
                description="Description for the first step on the recovery page, warning the user that they cannot log in without their recovery key"
              />
            </Box>
            <RecoveryCode id="recovery-code">
              {(recoveryCode.match(/.{4}/g) || []).map((item, index) => (
                <span key={index}>{item}</span>
              ))}
            </RecoveryCode>
            <DropdownMenuContainer id="mf-save-options">
              <DropdownMenu
                shouldRenderToParent
                trigger={({ triggerRef, ...props }) => (
                  <Button {...props} ref={triggerRef} iconAfter={<ChevronDownIcon label="" />}>
                    <FormattedMessage id="mf.recovery.saveoptions" defaultMessage="Save options" />
                  </Button>
                )}>
                <DropdownItemGroup>
                  <DropdownItem onClick={onClickOnRecovery}>
                    <div id="mf-copy-key">
                      <FormattedMessage id="mf.recovery.copy" defaultMessage="Copy to clipboard" />
                    </div>
                  </DropdownItem>
                  {!isEmbedded && (
                    <DropdownItem onClick={onRecoveryPrint}>
                      <div id="mf-print-key">
                        <FormattedMessage id="mf.recovery.print" defaultMessage="Print" />
                      </div>
                    </DropdownItem>
                  )}
                  <DropdownItem onClick={onRecoverySkip}>
                    <div id="mf-wrote-key">
                      <FormattedMessage id="mf.recovery.skip" defaultMessage="I wrote it down" />
                    </div>
                  </DropdownItem>
                </DropdownItemGroup>
              </DropdownMenu>
            </DropdownMenuContainer>
          </Box>
          <Box as="li" xcss={styles.listItem}>
            <Stack alignInline="center" space="space.100">
              <Inline space="space.100" alignBlock="center" grow="fill" spread="space-between">
                <Heading size="medium" as="h2">
                  <FormattedMessage
                    id="mf.recovery.seriously"
                    defaultMessage="2. Seriously, save this key!"
                    description="The second step on the recovery page, asking users to make sure they have saved the recovery key"
                  />
                </Heading>
                <StyledButton
                  id="two-step-verification-save-recovery-code-continue"
                  isDisabled={!newCodeSaved}
                  onClick={onComplete}>
                  <FormattedMessage id="mf.recovery.saved" defaultMessage="Saved, let's go" />
                </StyledButton>
              </Inline>
              <Image src={RecoveryIcon} width={imageWidth} alt="" />
            </Stack>
          </Box>
        </Stack>
      </Stack>
    );
  };

  const renderMobile = () => {
    return (
      <Stack alignInline="center" alignBlock="center">
        <Stack as="ol" alignInline="center" xcss={styles.orderedList}>
          <Box as="li" xcss={styles.listItem}>
            <Stack alignInline="center">
              <Heading size="small" as="h1">
                <FormattedMessage
                  id="mf.recovery.savekey.mobile"
                  defaultMessage="Save recovery key"
                />
              </Heading>
            </Stack>
            <Box xcss={styles.paragraph}>
              <FormattedMessage
                id="mf.recovery.savekey.desc.mobile"
                defaultMessage="If you lose access to your phone, you won't be able to log in to your account without the key. Save this key without letting anyone see it."
                description="Description warning the user that they cannot log in without their recovery key"
              />
            </Box>
            <input
              id="recovery-code-assistive"
              className="assistive"
              value={recoveryCode}
              readOnly
            />
            <MobileRecoveryCode value={(recoveryCode.match(/.{4}/g) || []).join(' ')} />
          </Box>
          <Box as="li" xcss={styles.listItem}>
            <TallButton appearance="primary" isFullWidth onClick={onComplete}>
              <FormattedMessage id="mf.recovery.saved" defaultMessage="Saved, let's go" />
            </TallButton>
          </Box>
        </Stack>
      </Stack>
    );
  };

  return (
    <Page>
      <Box xcss={styles.container}>
        <Hide below="sm">{renderDesktop()}</Hide>
        <Show below="sm">{renderMobile()}</Show>
      </Box>
    </Page>
  );
};

export default RecoveryPage;
